import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isAndroidApp } from "@/lib/platform";
import { showBanner, hideBanner } from "@/lib/native-ads";
import { isAdminTestAds, subscribeAdminTestAds } from "@/lib/admin-test-ads";

/**
 * Inline AdMob banner trigger.
 *
 * Free Android users only — UNLESS Admin Test Ads is on, in which case the
 * banner fires for the admin too (with Google test creatives) so placements
 * can be verified on a real device.
 */
export const InlineAdBanner = () => {
  const { user } = useAuth();
  const [isPaid, setIsPaid] = useState<boolean | null>(null);
  const [adminTest, setAdminTest] = useState<boolean>(isAdminTestAds());

  useEffect(() => subscribeAdminTestAds(setAdminTest), []);

  useEffect(() => {
    if (!user) { setIsPaid(false); return; }
    supabase
      .from("profiles")
      .select("credits_purchased_total")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => setIsPaid(Number(data?.credits_purchased_total ?? 0) > 0));
  }, [user?.id]);

  const eligible = isAndroidApp() && (adminTest || isPaid === false);

  useEffect(() => {
    if (!eligible) return;

    let shown = false;
    const THRESHOLD = 8;
    const check = () => {
      const doc = document.documentElement;
      const scrollBottom = window.scrollY + window.innerHeight;
      const atBottom = scrollBottom >= doc.scrollHeight - THRESHOLD;
      if (atBottom && !shown) {
        shown = true;
        void showBanner();
      } else if (!atBottom && shown) {
        shown = false;
        void hideBanner();
      }
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      void hideBanner();
    };
  }, [eligible]);

  if (!eligible) return null;

  return <div aria-hidden className="h-[72px] w-full" />;
};

export default InlineAdBanner;
